// Shared chrome for the module-06 floor screens (spec 06 §4.1/§4.2/§4.4/§4.5
// floor rows). Mobile-first, thumb-reachable, high-contrast, glove-usable:
// every interactive element here is at least 3rem tall, nothing depends on
// hover, and the primary action of each screen sits in a fixed bottom bar where
// a thumb reaches it one-handed.
//
// Everything in this file is presentational. It fetches nothing and decides no
// permission, so the pieces are unit-testable and the five floor screens cannot
// each invent their own version of "you are offline".

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { currentFloorPrincipal } from "../lib/floor-principal";
import { useFloorOutbox } from "../hooks/use-floor-outbox";
import { snapshotAgeParts } from "../lib/floor-read-cache";
import type { OutboxSnapshot } from "../lib/floor-outbox";
import type { FloorReadSource } from "../hooks/use-cached-read";

// ---------------------------------------------------------------------------
// Sync state
// ---------------------------------------------------------------------------

/**
 * The one connectivity statement in the floor app. It answers, in this order,
 * the only questions an operator has: is my work safe, is anything stuck, and
 * can the device even hold a queue?
 *
 * The pending count is ALWAYS rendered when non-zero — including online, where
 * it flickers through 1 and back to 0. That flicker is the point: it is how the
 * operator learns that the number means "not yet on the server", so that when
 * it sits at 14 in a dead zone they already know what they are looking at.
 */
export type SyncTone = "online" | "offline" | "attention" | "unreachable";

/**
 * The banner's tone, as a pure function so it can be tested against the case
 * that matters: `{ online: true, unreachable: true }`.
 *
 * `navigator.onLine` answers "is this device associated with a network", never
 * "can it reach us". The dominant plant failure is an access point with no
 * backhaul, where `onLine` is true, the pending count climbs, and a green bar
 * tells the operator — whose whole mental model is "green means my work is
 * going through" — exactly the wrong thing. So the QUEUE's own evidence wins:
 * repeated transport-class failures at the head mean unreachable, whatever the
 * browser thinks.
 */
export function syncTone(outbox: OutboxSnapshot, online: boolean): SyncTone {
  if (!online) return "offline";
  if (outbox.unreachable) return "unreachable";
  if (outbox.attentionCount > 0) return "attention";
  return "online";
}

export function SyncBanner({
  outbox,
  online,
  onSendNow,
}: {
  outbox: OutboxSnapshot;
  online: boolean;
  onSendNow?: () => void;
}) {
  const { t } = useTranslation();
  const blocked = outbox.blockedBy;
  const tone = syncTone(outbox, online);
  const toneClass = tone === "unreachable" ? "offline" : tone;

  return (
    <div className={`floor-sync floor-sync-${toneClass}`} data-testid="floor-sync" data-tone={tone}>
      <span className="floor-sync-state" role="status">
        <span className="floor-sync-dot" aria-hidden="true" />
        {tone === "offline"
          ? t("inventory.floor.sync.offline")
          : tone === "unreachable"
            ? t("inventory.floor.sync.unreachable")
            : t("inventory.floor.sync.online")}
      </span>

      {/* THE PENDING COUNT IS A WAY IN, not just a number (found by M07-S08
          driving a real airplane-mode inspection). Held work was reachable only
          through the attention link below, which renders only when something
          has FAILED — so an operator with four taps merely waiting to send
          could read "4 waiting to send" and had no way to open the tray and see
          WHICH four. Offline is exactly when they cannot type a URL either, and
          D-121 already ruled that held work is a LIST and not a count: that
          ruling reached the tray's contents but not the route to it. Client-side
          routing, so it works with the network dead. */}
      {outbox.pendingCount > 0 ? (
        <Link
          to="/floor/tray"
          className="floor-sync-pending floor-sync-pending-link"
          data-testid="floor-pending-count"
        >
          {t("inventory.floor.sync.pending", { count: outbox.pendingCount })}
        </Link>
      ) : (
        <span className="floor-sync-pending floor-sync-clear" data-testid="floor-pending-count">
          {t("inventory.floor.sync.allSynced")}
        </span>
      )}

      {outbox.attentionCount > 0 ? (
        <Link to="/floor/tray" className="floor-sync-tray" data-testid="floor-tray-link">
          {t("inventory.floor.sync.attention", { count: outbox.attentionCount })}
        </Link>
      ) : null}

      {/* "Send now" — the manual override. Backoff protects the server from a
          device that keeps failing; it must never be the reason an operator
          standing in solid wifi watches 14 items sit there with nothing to tap. */}
      {outbox.pendingCount > 0 && onSendNow !== undefined ? (
        <button
          type="button"
          className="floor-sync-send"
          onClick={onSendNow}
          data-testid="floor-send-now"
        >
          {t("inventory.floor.sync.sendNow")}
        </button>
      ) : null}

      {outbox.signedOut ? (
        <span className="floor-sync-note floor-sync-danger" data-testid="floor-signed-out">
          <Link to="/login">{t("inventory.floor.sync.signedOut")}</Link>
        </span>
      ) : null}

      {outbox.foreignCount > 0 ? (
        <span className="floor-sync-note floor-sync-danger" data-testid="floor-foreign-count">
          {t("inventory.floor.sync.otherUser", { count: outbox.foreignCount })}
        </span>
      ) : null}

      {blocked !== undefined && !outbox.signedOut ? (
        <span className="floor-sync-note" data-testid="floor-sync-blocked">
          {t("inventory.floor.sync.retrying", { attempts: blocked.attempts })}
        </span>
      ) : null}

      {!outbox.durable ? (
        <span className="floor-sync-note floor-sync-danger" data-testid="floor-not-durable">
          {t("inventory.floor.sync.notDurable")}
        </span>
      ) : null}

      {outbox.hydrateFailed ? (
        <span className="floor-sync-note floor-sync-danger" data-testid="floor-hydrate-failed">
          {t("inventory.floor.sync.hydrateFailed")}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read freshness
// ---------------------------------------------------------------------------

/**
 * Age note for a screen rendering from the offline snapshot. Only shown for a
 * CACHED read — a live one needs no caveat, and adding one everywhere would
 * teach the operator to ignore it.
 */
export function SnapshotNote({
  source,
  cachedAt,
  revalidating = false,
  now = Date.now(),
}: {
  source: FloorReadSource;
  cachedAt: number | undefined;
  revalidating?: boolean;
  now?: number;
}) {
  const { t } = useTranslation();
  if (source !== "cached" || cachedAt === undefined) return null;
  const age = snapshotAgeParts(Math.max(0, now - cachedAt));
  return (
    <p className="floor-snapshot-note" role="note" data-testid="floor-snapshot-note">
      {t("inventory.floor.cache.showingSnapshot")}{" "}
      <span className="floor-snapshot-age">{t(age.key, { count: age.n })}</span>
      {revalidating ? (
        <span className="floor-snapshot-checking" data-testid="floor-snapshot-checking">
          {" · "}
          {t("inventory.floor.cache.checking")}
        </span>
      ) : null}
    </p>
  );
}

/**
 * THE TAP WAS NOT SAVED — the durable write itself failed (a full device, a
 * store the browser tore down). Deliberately the loudest state in the file: it
 * is the only one where the operator has to do the work again, and a quiet
 * version of it is indistinguishable from success.
 */
export function FloorNotSaved({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="floor-state floor-state-error" role="alert" data-testid="floor-not-saved">
      <p className="floor-state-title">{t("inventory.floor.sync.notSavedTitle")}</p>
      <p className="floor-state-body">{t("inventory.floor.sync.notSavedBody")}</p>
      {onRetry !== undefined ? (
        <button type="button" className="floor-btn floor-btn-primary" onClick={onRetry}>
          {t("inventory.common.retry")}
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States (§4: every screen has empty / error / loading)
// ---------------------------------------------------------------------------

export function FloorSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="floor-skeleton" data-testid="floor-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} className="skeleton" />
      ))}
    </div>
  );
}

export function FloorEmpty({
  title,
  body,
  ctaLabel,
  onCta,
}: {
  title: string;
  body: string;
  ctaLabel?: string;
  onCta?: () => void;
}) {
  return (
    <div className="floor-state floor-state-empty" data-testid="floor-empty" role="status">
      <p className="floor-state-title">{title}</p>
      <p className="floor-state-body">{body}</p>
      {ctaLabel !== undefined && onCta !== undefined ? (
        <button type="button" className="floor-btn floor-btn-primary" onClick={onCta}>
          {ctaLabel}
        </button>
      ) : null}
    </div>
  );
}

export function FloorError({
  title,
  detail,
  requestId,
  onRetry,
}: {
  title: string;
  detail: string;
  requestId?: string | undefined;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="floor-state floor-state-error" role="alert" data-testid="floor-error">
      <p className="floor-state-title">{title}</p>
      <p className="floor-state-body">{detail}</p>
      {requestId !== undefined && requestId.length > 0 ? (
        <p className="floor-state-rid">
          {t("inventory.common.requestId")}: <code>{requestId}</code>
        </p>
      ) : null}
      {onRetry !== undefined ? (
        <button type="button" className="floor-btn" onClick={onRetry}>
          {t("inventory.common.retry")}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Offline with nothing cached. Distinct from "empty" on purpose: "no lines on
 * this worksheet" and "this device has never seen this worksheet" are opposite
 * facts, and only one of them is fixed by finding signal.
 */
export function FloorNeedsConnection({ what }: { what: string }) {
  const { t } = useTranslation();
  return (
    <div className="floor-state floor-state-empty" role="status" data-testid="floor-needs-network">
      <p className="floor-state-title">{t("inventory.floor.cache.needConnectionTitle")}</p>
      <p className="floor-state-body">{t("inventory.floor.cache.needConnectionBody", { what })}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/**
 * WHO IS SIGNED IN (D-119, as narrowed by D-425 and D-444).
 *
 * A mounted floor tablet is shared, and every quantity, defect and downtime
 * minute it sends is booked to whoever is signed in, so the floor always says
 * who that is. It says it ONCE, in the floor's own top bar beside Sign out
 * (floor-layout.tsx), and no longer under every screen title, where the owner's
 * review read it as clutter (D-444 ruling 1).
 *
 * An UNRECORDED identity draws nothing while the queue is empty (D-425): a
 * session this browser never signed in through stamped no name, and with
 * nothing queued there is nothing to misattribute. With work queued it is the
 * one case nothing else protects. The outbox's principal check
 * (`isOwnPrincipal`) answers YES whenever the current principal is unknown, so
 * worker A's queue would drain as whoever's session the tablet comes back
 * with. That case is a single banner across the top of the floor asking for a
 * fresh sign-in, which stamps the name, so the outbox can hold A's work back
 * from B.
 */
export function SignedInAs() {
  const { t } = useTranslation();
  const who = currentFloorPrincipal();
  if (who === undefined) return <UnstampedPrincipal />;
  return (
    <p className="floor-principal" data-testid="floor-principal">
      {t("inventory.floor.principal.signedIn", { who })}
    </p>
  );
}

/** No name stamped: silent with an empty queue, a warning with work on it. */
function UnstampedPrincipal() {
  const { t } = useTranslation();
  const outbox = useFloorOutbox();
  const queued = outbox.pendingCount + outbox.attentionCount + outbox.reviewCount;
  if (!(queued > 0)) return null;
  return (
    <p
      className="floor-principal floor-principal-unknown"
      role="alert"
      data-testid="floor-principal"
      data-state="unknown"
    >
      {t("inventory.floor.principal.signInAgain")}
    </p>
  );
}

export function FloorScreen({
  title,
  back,
  children,
  actions,
  wide = false,
}: {
  title: string;
  /** A `<Link>` supplied by the screen — TanStack `to` is route-typed, so the
   * shell takes the rendered element rather than a stringly-typed path. */
  back?: ReactNode;
  children: ReactNode;
  /** The fixed bottom action bar — thumb-reachable, one primary action. */
  actions?: ReactNode;
  /** A hub or a list: from 1024 px it uses the tablet's width and lays its
   * cards in two columns (D-444 ruling 3). A form stays one column. */
  wide?: boolean;
}) {
  return (
    <section
      className={wide ? "floor-screen floor-screen-wide" : "floor-screen"}
      data-testid="floor-screen"
    >
      <header className="floor-screen-head">
        {back}
        <h1>{title}</h1>
      </header>
      <div className="floor-screen-body">{children}</div>
      {actions !== undefined ? <div className="floor-actionbar">{actions}</div> : null}
    </section>
  );
}

/** A full-width, `--floor-target`-tall target. The only button shape on the
 * floor.
 *
 * `pressed` is for a button that is one of a MUTUALLY EXCLUSIVE SET — the
 * cut/re-measure mode pair, the issue/return toggle, a picked scrap reason. It
 * emits `aria-pressed`, which is the accessible name for "this one is the one
 * that is on"; the floor deliberately does not use the office's tab register
 * for these, because a 36 px underline strip is not a glove target (§3.10 and
 * the note on `floor-unit.tsx`). Omitted entirely when undefined, so an
 * ordinary action button does not claim a toggle it is not. */
export function FloorButton({
  onClick,
  children,
  variant = "default",
  disabled = false,
  disabledReason,
  pressed,
  type = "button",
  testId,
}: {
  onClick?: () => void;
  children: ReactNode;
  variant?: "default" | "primary" | "danger" | "ghost";
  disabled?: boolean;
  /**
   * WHY THIS BUTTON IS DEAD, rendered AT THE BUTTON (D-118, surface finding 1).
   *
   * A disabled floor control with its explanation somewhere else on the page is
   * indistinguishable from a broken one. Measured on the op screen: four submit
   * buttons dead because the tablet had not picked which registered device it
   * is, their only account of it a 15 px hint 580 px up the scroll — an
   * operator keys a quantity, scrolls to Report, taps a button labelled
   * "Report" and nothing happens, with nothing on screen to read.
   *
   * The reason goes INSIDE the button, not beside it, for two reasons: it is
   * then part of the accessible name (a screen reader announces the refusal
   * with the action), and it cannot be scrolled away from the control it
   * belongs to. It is the pattern `floor-count.tsx` already ships — a submit
   * that re-labels itself "16 line(s) still to count" — generalised so no floor
   * screen has to reinvent it.
   *
   * Only rendered when the button IS disabled. A caller whose control has more
   * than one way to be dead passes the reason for the one that is live now.
   */
  disabledReason?: string | undefined;
  pressed?: boolean;
  type?: "button" | "submit";
  testId?: string;
}) {
  const explained = disabled && disabledReason !== undefined && disabledReason.length > 0;
  return (
    <button
      type={type === "submit" ? "submit" : "button"}
      className={`floor-btn floor-btn-${variant}${explained ? " floor-btn-explained" : ""}`}
      onClick={onClick}
      disabled={disabled}
      {...(pressed === undefined ? {} : { "aria-pressed": pressed })}
      data-testid={testId}
    >
      <span className="floor-btn-label">{children}</span>
      {explained ? (
        <span className="floor-btn-reason" data-testid={testId ? `${testId}-reason` : undefined}>
          {disabledReason}
        </span>
      ) : null}
    </button>
  );
}
